import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, fmtNum, today, fmtDate } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';
import ConfirmModal from '../../components/ConfirmModal';
import './Employees.css';

const EMPTY = {
  emp_code: '', name: '', phone: '', designation: '',
  base_salary: '', ot_rate_per_hr: '', joined_on: '', is_active: true,
};

// Pay-head metadata for the Salary / Advance dialogs. `pt` is the payment_type
// sent to payments.php (salary → Dr Salary Expense; advance → Dr Employee
// Advances asset). Both auto-create one Payments (Money Out) record only —
// never an Expenses entry — so nothing is double-counted.
const PAY_HEADS = {
  salary:  { pt: 'salary',  title: 'Pay Salary',   verb: 'Pay salary',  histTitle: 'Salary History' },
  advance: { pt: 'advance', title: 'Give Advance', verb: 'Give advance', histTitle: 'Advance History' },
};

export default function Employees() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  // { employee, head } for the Pay Salary / Give Advance dialog, and for history
  const [payDlg, setPayDlg] = useState(null);
  const [histDlg, setHistDlg] = useState(null);
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const { data: employees, loading, error, reload } = useApi(() => api.employees.list(), []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });
  const banks = banksData.banks || [];

  const startEdit = (e) => {
    setForm({
      emp_code: e.emp_code, name: e.name, phone: e.phone || '',
      designation: e.designation || '',
      base_salary: e.base_salary || '', ot_rate_per_hr: e.ot_rate_per_hr || '',
      joined_on: e.joined_on || '',
      is_active: !!Number(e.is_active),
    });
    setEditId(e.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { notify('❌ ' + t('employees.errors.nameRequired')); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_salary: parseFloat(form.base_salary || 0),
        ot_rate_per_hr: parseFloat(form.ot_rate_per_hr || 0),
        is_active: form.is_active ? 1 : 0,
      };
      if (editId) await api.employees.update(editId, payload);
      else await api.employees.create(payload);
      notify(editId ? '✅ ' + t('employees.toasts.updated') : '✅ ' + t('employees.toasts.added'));
      setShowForm(false); setForm(EMPTY); setEditId(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.employees.remove(confirm.id);
      notify('✅ ' + t('employees.toasts.deactivated'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  return (
    <div className="em-page">
      <div className="page-header">
        <h2 className="page-title">{t('employees.title')}</h2>
        <Can module="employees" action="create">
          <button className="btn btn-primary btn-sm"
            onClick={() => { setShowForm(!showForm); setForm(EMPTY); setEditId(null); }}>
            {showForm ? '✕ ' + t('common.cancel') : '＋ ' + t('employees.newEmployee')}
          </button>
        </Can>
      </div>

      {msg && <div className="em-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? t('employees.editEmployee') : t('employees.addNewEmployee')}</div>
          <div className="em-form-grid">
            <div className="form-group">
              <label>{t('employees.employeeCode')} {editId ? '' : t('employees.autoGenerateHint')}</label>
              <input value={form.emp_code} disabled={!!editId}
                onChange={e => setForm({ ...form, emp_code: e.target.value.toUpperCase() })}
                placeholder={t('employees.empCodePlaceholder')} />
            </div>
            <div className="form-group">
              <label>{t('common.name')} *</label>
              <input value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('employees.phone')}</label>
              <input value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('employees.designation')}</label>
              <input value={form.designation}
                onChange={e => setForm({ ...form, designation: e.target.value })}
                placeholder={t('employees.designationPlaceholder')} />
            </div>
            <div className="form-group">
              <label>{t('employees.baseSalary')}</label>
              <input type="number" step="0.01" value={form.base_salary}
                onChange={e => setForm({ ...form, base_salary: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('employees.otRatePerHour')} *</label>
              <input type="number" step="0.01" value={form.ot_rate_per_hr}
                onChange={e => setForm({ ...form, ot_rate_per_hr: e.target.value })} />
            </div>
            <div className="form-group">
              <label>{t('employees.joinedOn')} *</label>
              <input type="date" value={form.joined_on}
                onChange={e => setForm({ ...form, joined_on: e.target.value })} />
            </div>
            {editId && (
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', margin: 0 }}>
                  <input type="checkbox" checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                  {t('common.active')}
                </label>
              </div>
            )}
          </div>
          <div style={{ padding: '0 12px 12px' }}>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? '⏳ ' + t('common.saving') : (editId ? t('common.update') : t('employees.addEmployee'))}
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{t('employees.staff', { count: (employees || []).length })}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t('common.code')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.name')}</th>
                <th style={{ textAlign: 'left' }}>{t('employees.designation')}</th>
                <th style={{ textAlign: 'left' }}>{t('employees.phone')}</th>
                <th>{t('employees.baseSalaryShort')}</th>
                <th>{t('employees.otRate')}</th>
                <th>{t('common.status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(employees || []).length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  {t('employees.emptyEmployees')}
                </td></tr>
              ) : (employees || []).map(e => (
                <tr key={e.id} style={{ opacity: Number(e.is_active) === 0 ? 0.5 : 1 }}>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace', fontWeight: 600 }}>{e.emp_code}</td>
                  <td style={{ textAlign: 'left' }}>{e.name}</td>
                  <td style={{ textAlign: 'left' }}>{e.designation || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{e.phone || '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>
                    {Number(e.base_salary) > 0 ? `₹${fmtNum(e.base_salary)}` : '—'}
                  </td>
                  <td style={{ fontFamily: 'monospace' }}>
                    ₹{fmtNum(e.ot_rate_per_hr)}/hr
                  </td>
                  <td>
                    <span className={`em-status ${Number(e.is_active) === 1 ? 'em-active' : 'em-inactive'}`}>
                      {Number(e.is_active) === 1 ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Can module="payments" action="create">
                      <button className="btn btn-sm" title={t('employees.paySalary')}
                        onClick={() => setPayDlg({ employee: e, head: 'salary' })}>💰</button>
                      <button className="btn btn-sm" title={t('employees.giveAdvance')}
                        onClick={() => setPayDlg({ employee: e, head: 'advance' })}>🤝</button>
                    </Can>
                    <Can module="payments" action="read">
                      <button className="btn btn-sm" title={t('employees.salaryHistory')}
                        onClick={() => setHistDlg({ employee: e, head: 'salary' })}>📜</button>
                      <button className="btn btn-sm" title={t('employees.advanceHistory')}
                        onClick={() => setHistDlg({ employee: e, head: 'advance' })}>🧾</button>
                    </Can>
                    <Can module="employees" action="update">
                      <button className="btn btn-sm" title={t('common.edit')} onClick={() => startEdit(e)}>✏️</button>
                    </Can>
                    {Number(e.is_active) === 1 && (
                      <Can module="employees" action="delete">
                        <button className="btn btn-sm" title={t('employees.deactivate')} style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirm(e)}>🗑</button>
                      </Can>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('employees.deactivateTitle')}
        message={confirm ? t('employees.deactivateMsg', { name: confirm.name }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={t('employees.deactivate')}
      />

      {payDlg && (
        <PayDialog
          employee={payDlg.employee}
          head={PAY_HEADS[payDlg.head]}
          banks={banks}
          onClose={() => setPayDlg(null)}
          onDone={(m) => { setPayDlg(null); notify(m); }}
        />
      )}

      {histDlg && (
        <HistoryDialog
          employee={histDlg.employee}
          head={PAY_HEADS[histDlg.head]}
          onClose={() => setHistDlg(null)}
        />
      )}
    </div>
  );
}

// ─── Pay Salary / Give Advance dialog ────────────────────────
// Creates exactly one Payments (Money Out) record (employee_id + payment_type).
// The backend posts the ledger pair (salary → Dr Salary Expense; advance → Dr
// Employee Advances) — no Expenses entry, so there's no duplication.
function PayDialog({ employee, head, banks, onClose, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    payment_date: today(),
    paid_amount: head.pt === 'salary' && Number(employee.base_salary) > 0
      ? String(employee.base_salary) : '',
    mode: 'Cash',
    reference_no: '',
    remarks: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    const amt = parseFloat(form.paid_amount || 0);
    if (!(amt > 0)) { setErr(t('employees.errors.amountGtZero')); return; }
    setSaving(true); setErr('');
    try {
      await api.payments.create({
        payment_date: form.payment_date,
        employee_id:  employee.id,
        party_name:   employee.name,
        payment_type: head.pt,
        paid_amount:  amt,
        mode:         form.mode,
        reference_no: form.reference_no,
        remarks:      form.remarks,
      });
      onDone('✅ ' + t('employees.recordedFor', { verb: t('employees.payHeads.' + head.pt + '.verb'), name: employee.name }));
    } catch (e) {
      setErr(e.message || t('common.failedToSave'));
      setSaving(false);
    }
  };

  return (
    <div className="em-modal-overlay" onClick={onClose}>
      <div className="em-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="section-header">{t('employees.payHeads.' + head.pt + '.title')} — {employee.name}</div>
        <div style={{ padding: 12, display: 'grid', gap: 10 }}>
          <div className="form-group">
            <label>{t('common.date')} *</label>
            <input type="date" value={form.payment_date}
              onChange={e => setForm({ ...form, payment_date: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t('employees.amountRupee')} *</label>
            <input type="number" step="0.01" value={form.paid_amount} autoFocus
              onChange={e => setForm({ ...form, paid_amount: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t('employees.mode')} *</label>
            <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
              <option value="Cash">{t('common.cash')}</option>
              {banks.map(b => (
                <option key={b.id} value={`Bank:${b.id}`}>
                  {b.name}{Number(b.is_default) === 1 ? ' ' + t('employees.defaultSuffix') : ''}
                </option>
              ))}
              <option value="UPI">{t('employees.upiDefaultBank')}</option>
              <option value="NEFT">{t('employees.neftDefaultBank')}</option>
              <option value="Cheque">{t('employees.chequeDefaultBank')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('employees.referenceNo')}</label>
            <input value={form.reference_no}
              onChange={e => setForm({ ...form, reference_no: e.target.value })} />
          </div>
          <div className="form-group">
            <label>{t('common.remarks')}</label>
            <input value={form.remarks}
              onChange={e => setForm({ ...form, remarks: e.target.value })} />
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-sm" onClick={onClose} disabled={saving}>{t('common.cancel')}</button>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>
              {saving ? '⏳ ' + t('common.saving') : t('employees.payHeads.' + head.pt + '.verb')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Salary History / Advance History dialog ─────────────────
function HistoryDialog({ employee, head, onClose }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    api.payments.history(employee.id, head.pt)
      .then(d => { if (alive) setRows(Array.isArray(d) ? d : (d?.rows || d?.data || [])); })
      .catch(e => { if (alive) setError(e.message || t('common.failedToLoad')); });
    return () => { alive = false; };
  }, [employee.id, head.pt]);

  const total = (rows || []).reduce((s, r) => s + Number(r.paid_amount || 0), 0);

  return (
    <div className="em-modal-overlay" onClick={onClose}>
      <div className="em-modal" onClick={(ev) => ev.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="section-header">{t('employees.payHeads.' + head.pt + '.histTitle')} — {employee.name}</div>
        <div style={{ padding: 12 }}>
          {error && <ErrBanner msg={error} />}
          {rows === null ? <Spinner /> : rows.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 16 }}>
              {t('employees.noHistoryYet', { title: t('employees.payHeads.' + head.pt + '.histTitleLower') })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>{t('common.date')}</th>
                    <th style={{ textAlign: 'left' }}>{t('employees.paymentNo')}</th>
                    <th style={{ textAlign: 'left' }}>{t('employees.mode')}</th>
                    <th style={{ textAlign: 'left' }}>{t('employees.ref')}</th>
                    <th style={{ textAlign: 'right' }}>{t('common.amount')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={{ textAlign: 'left' }}>{fmtDate(r.payment_date)}</td>
                      <td style={{ textAlign: 'left', fontFamily: 'monospace' }}>{r.payment_no}</td>
                      <td style={{ textAlign: 'left' }}>{r.mode}</td>
                      <td style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>{r.reference_no || '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{fmtNum(r.paid_amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'right', fontWeight: 700 }}>{t('common.total')}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>₹{fmtNum(total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="btn btn-sm" onClick={onClose}>{t('common.close')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
